import React, { useEffect, useState, useMemo } from "react";
import "../../../css/QavachStaffTable.css"; // We will update this file next
import ViewQavachStaffModal from "./ViewQavachStaffModal";
import EditQavachStaffModal from "./EditQavachStaffModal";
import api from '../../../api/axiosConfig';

// --- Interfaces (No Changes) ---
interface RoleId {
  _id: string;
  name: string;
}
interface RoleAssignment {
  _id: string;
  userId: string;
  roleId: RoleId;
  status: 'ACTIVE' | 'DISABLED' | 'UNASSIGNED';
  assignedAt: string;
  disabledAt?: string | null;
  assignedBy: string;
  createdAt: string;
  updatedAt: string;
  notes: string;
  __v: number;
}
interface UserWithRoles {
  _id: string;
  email: string;
  displayName: string;
  roleType: string;
  entraObjectId: string;
  roleAssignments: RoleAssignment[];
}
interface DisplayUser {
  _id: string;
  email: string;
  displayName: string;
  roleType: string;
  status: string;
  role: string;
  createdAt: string;
  disabledAt?: string | null;
}
// --- Logic (No Changes) ---
const QavachStaffTable: React.FC = () => {
  const [displayStaff, setDisplayStaff] = useState<DisplayUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<DisplayUser | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      const response = await api.auth_api.get<UserWithRoles[]>("/users/with-roles/full", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const displayUsers: DisplayUser[] = response.data.map(user => {
        if (user.roleAssignments && user.roleAssignments.length > 0) {
          const roleAssignment = user.roleAssignments[0];
          return {
            _id: user._id,
            email: user.email,
            displayName: user.displayName,
            roleType: user.roleType,
            status: roleAssignment.status,
            role: roleAssignment.roleId.name,
            createdAt: roleAssignment.assignedAt,
            disabledAt: roleAssignment.disabledAt
          };
        }
        return {
          _id: user._id,
          email: user.email,
          displayName: user.displayName,
          roleType: user.roleType,
          status: "UNASSIGNED",
          role: "-",
          createdAt: "-",
          disabledAt: null
        };
      });
      setDisplayStaff(displayUsers);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredStaff = useMemo(() => {
    return displayStaff
      .filter(user => {
        return (
          user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase())
        );
      })
      .filter(user => {
        if (roleFilter === "All") return true;
        return user.role === roleFilter;
      })
      .filter(user => {
        if (statusFilter === "All") return true;
        return user.status.toUpperCase() === statusFilter.toUpperCase();
      });
  }, [displayStaff, searchTerm, roleFilter, statusFilter]);

  const uniqueRoles = useMemo(() => {
    const roles = new Set(displayStaff.map(user => user.role).filter(role => role !== "-"));
    return ["All Roles", ...Array.from(roles)];
  }, [displayStaff]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, statusFilter]);

  const totalPages = Math.ceil(filteredStaff.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentStaffToDisplay = filteredStaff.slice(startIndex, endIndex);

  const handleRowClick = (user: DisplayUser) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };
  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedUser(null);
  };
  const handleEdit = () => {
    setIsViewModalOpen(false);
    setIsEditModalOpen(true);
  };
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedUser(null);
  };
  const handleUpdate = (updatedUser: DisplayUser) => {
    setDisplayStaff(prev => prev.map(user => (user._id === updatedUser._id ? updatedUser : user)));
    setIsEditModalOpen(false);
  };
  const handleDisable = () => console.log("Disable user:", selectedUser);

  return (
    // This div is just a wrapper, it no longer needs CSS
    <div className="onboard-users-table-container"> {/* Renamed */}
      <div className="onboard-users-table-header"> {/* Renamed */}
        <div className="global-filters-container">
          <div className="global-search-wrapper">
            <svg
              className="global-search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder="Search by name or email..."
              className="global-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="global-select-input"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            {uniqueRoles.map(role => (
              <option key={role} value={role === "All Roles" ? "All" : role}>{role}</option>
            ))}
          </select>
          <select
            className="global-select-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="DISABLED">Disabled</option>
            <option value="UNASSIGNED">Unassigned</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p>Loading staff...</p>
      ) : (
        <div className="table-responsive-wrapper">
          <table className="onboard-users-table staff-table">
            <thead>
              <tr>
                <th style={{ width: "60px", textAlign: "center" }}>No.</th>
                <th>User ID</th>
                <th>User Name</th>
                <th>Category</th>
                <th>Status</th>
                <th>Role / Sub Role</th>
                <th>Created On</th>
                <th>Disabled On</th>
              </tr>
            </thead>
            <tbody>
              {currentStaffToDisplay.length > 0 ? (
                currentStaffToDisplay.map((s, index) => (
                  <tr key={s._id} onClick={() => handleRowClick(s)} style={{ cursor: "pointer" }}>
                    <td style={{ textAlign: "center" }}>{startIndex + index + 1}</td>
                    <td>{s.email}</td>
                    <td><b>{s.displayName}</b></td>
                    <td>
                      <span className="onboard-users-badge onboard-users-badge-role">
                        {'QAVACH STAFF'}
                      </span>
                    </td>
                    <td>
                      <span className={`onboard-users-badge onboard-users-status-${(s.status || "unassigned").toLowerCase()}`}>
                        {s.status || "Unassigned"}
                      </span>
                    </td>
                    <td>{s.role}</td>
                    <td>
                      {s.createdAt && s.createdAt !== "-"
                        ? new Date(s.createdAt).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>
                      {s.disabledAt
                        ? new Date(s.disabledAt).toLocaleDateString()
                        : "-"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={8} style={{ textAlign: "center" }}>No staff found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {isViewModalOpen && selectedUser && <ViewQavachStaffModal user={selectedUser} onClose={handleCloseViewModal} onEdit={handleEdit} onDisable={handleDisable} />}
      {isEditModalOpen && selectedUser && <EditQavachStaffModal user={selectedUser} onClose={handleCloseEditModal} onUpdate={handleUpdate} />}

      {/* Pagination */}
      {totalPages > 0 && (
        <div className="onboard-users-pagination-footer"> {/* Renamed */}
          <p>
            Showing {startIndex + 1} to {Math.min(endIndex, filteredStaff.length)} of {filteredStaff.length} results
          </p>
          <div className="onboard-users-pagination"> {/* Renamed */}
            <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
              <button key={pageNumber} onClick={() => setCurrentPage(pageNumber)} className={currentPage === pageNumber ? "active" : ""}>
                {pageNumber}
              </button>
            ))}
            <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QavachStaffTable;